import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Star, Users, TrendingUp, Package } from "lucide-react";
import { useEmailCapture } from "../components/EmailCaptureProvider";

export const Hero = () => {
  const { openModal } = useEmailCapture();
  const [stock, setStock] = useState(172);

  useEffect(() => {
    const interval = setInterval(() => {
      setStock(prev => (prev > 50 ? prev - 1 : 172));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section data-testid="hero-section" className="bg-gradient-to-b from-[#FFF8F0] via-white to-[#FFF5E6] pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" aria-labelledby="main-heading">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B00]/5 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#FFB347]/10 rounded-full blur-3xl" aria-hidden="true" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Headline - Clickable */}
        <div onClick={openModal} className="cursor-pointer">
          <div className="text-center mb-8 hover:opacity-90 transition-opacity">
            <span className="inline-block bg-[#C41E3A] text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 animate-bounce" role="status">
              🔥 VIRAL DISCOVERY - AS SEEN ON TV 🔥
            </span>
            
            <h1 id="main-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-[#1A1C19] mb-4" style={{ fontFamily: "Fraunces, serif" }}>
              Do This <span className="text-[#FF6B00]">"Orange Peel Trick"</span>
              <br />
              And <span className="underline decoration-[#FF6B00] decoration-4">Never</span> Wear XL Again
            </h1>
            
            <p className="text-lg md:text-xl text-[#4A5043] max-w-2xl mx-auto mb-6">
              Discover the 10-second Spanish morning ritual that's helping thousands melt stubborn fat — 
              <span className="font-bold text-[#1A1C19]"> without dieting or exercise.</span>
            </p>

            {/* Rating */}
            <div className="flex items-center justify-center gap-2 mb-6" aria-label="Customer rating: 4.9 out of 5 stars based on 12,847 reviews">
              <div className="flex items-center gap-0.5" role="img" aria-label="5 stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} fill="#FF6B00" className="text-[#FF6B00]" aria-hidden="true" />
                ))}
              </div>
              <span className="font-bold text-[#1A1C19]">4.9/5</span>
              <span className="text-[#73786F]">• 12,847+ Reviews</span>
            </div>
          </div>
        </div>

        {/* YouTube Video Section */}
        <figure className="max-w-4xl mx-auto mb-10">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#FF6B00]">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/5UojDPa9jUA?rel=0&modestbranding=1"
                title="Orange Peel Trick Video - Learn how the Spanish morning ritual helps burn stubborn fat naturally"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
          <figcaption className="text-center text-sm text-[#73786F] mt-3">
            👆 Watch the video above to discover the shocking truth
          </figcaption>
        </figure>

        {/* Urgency Stats - Clickable */}
        <div onClick={openModal} className="cursor-pointer">
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
            <div className="bg-white rounded-xl p-4 shadow-lg border border-[#FFE4CC] text-center hover:shadow-xl transition-shadow">
              <Users className="text-[#FF6B00] mx-auto mb-2" size={24} aria-hidden="true" />
              <p className="text-2xl font-black text-[#1A1C19]">12,847+</p>
              <p className="text-xs text-[#73786F]">Happy Customers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border border-[#FFE4CC] text-center hover:shadow-xl transition-shadow">
              <TrendingUp className="text-[#2E7D32] mx-auto mb-2" size={24} aria-hidden="true" />
              <p className="text-2xl font-black text-[#1A1C19]">27 lbs</p>
              <p className="text-xs text-[#73786F]">Avg. Weight Lost</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border border-[#C41E3A] text-center animate-pulse hover:shadow-xl transition-shadow">
              <Package className="text-[#C41E3A] mx-auto mb-2" size={24} aria-hidden="true" />
              <p className="text-2xl font-black text-[#C41E3A]">{stock}</p>
              <p className="text-xs text-[#73786F]">Bottles Left Today</p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            onClick={openModal}
            data-testid="hero-cta-primary"
            className="w-full sm:w-auto bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] hover:from-[#E65C00] hover:to-[#FF6B00] text-white font-black rounded-full px-10 py-5 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 text-lg uppercase tracking-wide"
          >
            👉 Yes! I Want To Burn Fat Fast
          </Button>
        </div>

        <div onClick={openModal} className="cursor-pointer">
          <p className="text-center text-sm text-[#73786F] mt-4 hover:text-[#FF6B00] transition-colors">
            ⚡ FREE Shipping • 🛡️ 180-Day Money Back Guarantee • 🔒 Secure Checkout
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
